export const formatClientName = (
    first: string | null | undefined,
    last: string | null | undefined,
    fallback: string,
) => {
    const name = [first, last].filter(Boolean).join(' ').trim()
    return name || fallback
}

export const formatMoney = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—'
    const numeric = typeof value === 'number' ? value : parseFloat(String(value))
    if (Number.isNaN(numeric)) return '—'
    return numeric.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 2,
    })
}

/** Compact money for stat tiles, e.g. £4.92m / £86.8k. */
export const formatMoneyCompact = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—'
    const numeric = typeof value === 'number' ? value : parseFloat(String(value))
    if (Number.isNaN(numeric)) return '—'
    const abs = Math.abs(numeric)
    if (abs >= 1_000_000) return `£${(numeric / 1_000_000).toFixed(2)}m`
    if (abs >= 1_000) return `£${(numeric / 1_000).toFixed(1)}k`
    return formatMoney(numeric)
}

export const formatDate = (value: string | null | undefined) => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return String(value)
    return parsed.toLocaleDateString('en-GB')
}

export const formatCount = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : value.toLocaleString('en-GB')

export const formatFieldLabel = (key: string) => {
    const overrides: Record<string, string> = {
        batch_group: 'Funding batch',
        funding_batch_id: 'Funding batch ID',
        nera_id: 'NERA ID',
        funder_case_ref: 'Funder case ref',
        funder_client_id: 'Funder client ID',
        introducer_case_ref: 'Introducer case ref',
        starting_balance: 'Loan value (starting balance)',
        loan_value: 'Loan value (recorded fee)',
    }
    if (overrides[key]) return overrides[key]

    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\bAte\b/, 'ATE')
        .replace(/\bIva\b/, 'IVA')
        .replace(/\bCfa\b/, 'CFA')
        .replace(/\bFos\b/, 'FOS')
        .replace(/\bDob\b/, 'DOB')
}
export const formatFieldValue = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    const str = String(value)
    // MySQL DATE/DATETIME values arrive as ISO strings; show them in UK format.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) return new Date(str).toLocaleDateString('en-GB')
    return str
}

/** Field groups rendered in the claim detail drawer, in display order. */
export const DETAIL_SECTIONS: { title: string; keys: string[] }[] = [
    {
        title: 'Claim',
        keys: [
            'funder_case_ref',
            'defendant',
            'agreement_number',
            'plate_number',
            'law_firm_secondary_reference',
            'facility',
        ],
    },
    {
        title: 'Client',
        keys: [
            'funder_client_id',
            'client_title',
            'client_first_name',
            'client_last_name',
            'client_dob',
            'client_email',
            'client_phone',
            'address_line_1',
            'address_line_2',
            'address_city',
            'address_county',
            'address_post_code',
            'address_country',
        ],
    },
    {
        title: 'Quantum & loan',
        keys: [
            'quantum_individual',
            'quantum_client_cumulative',
            'starting_balance',
            'loan_value',
            'total_amount_payable',
            'monthly_repayment',
            'commission_rate',
            'stat_interest',
            'calculation_date',
        ],
    },
    {
        title: 'Agreement terms',
        keys: [
            'agreement_start_date',
            'agreement_end_date',
            'contract_term',
            'account_type',
            'lender_type',
            'agreement_status',
        ],
    },
    {
        title: 'Insurance (ATE)',
        keys: [
            'ate_provider',
            'ate_policy_number',
            'ate_policy_id',
            'ate_inception_date',
            'ate_indemnity_limit',
            'ate_premium',
            'ate_underwriter',
            'ate_underwriter_email',
        ],
    },
    {
        title: 'Case status',
        keys: [
            'cfa_date',
            'incident_date',
            'limitation_end_date',
            'outside_limitation',
            'loc_sent_date',
            'client_status',
            'iva',
            'closed',
            'fos_rejected',
            'affordability',
            'cancelled',
            'cancellation_date',
        ],
    },
    {
        title: 'Funding & provenance',
        keys: [
            'batch_group',
            'funding_batch_id',
            'nera_id',
            'allocated',
            'submitted',
            'api_transferred',
            'api_transferred_date',
            'law_firm',
            'fee_earner',
            'base_claim',
            'pdf_claim',
            'origin',
            'experian_reference',
            'enrichment_status',
            'enrichment_error',
            'enriched_at',
            'captured_at',
        ],
    },
]

/**
 * Document groups for the claim drawer, in display order.
 *
 * Claims packaged as a base claim carry `base`-prefixed variants of the same
 * documents, so both spellings map to the same group.
 */
export const DOCUMENT_GROUPS: { title: string; keys: string[] }[] = [
    {
        title: 'Funding pack',
        keys: ['documentsPack', 'bundle', 'baseBundle'],
    },
    {
        title: 'Signed documents',
        keys: [
            'cfa',
            'baseCfa',
            'loa',
            'baseLoa',
            'aof',
            'baseAof',
            'loc',
            'baseLoc',
            'esignCertificate',
            'baseEsignCertificate',
        ],
    },
    {
        title: 'Technical reports',
        keys: [
            'trReport',
            'baseTrReport',
            'trForensicsReport',
            'baseTrForensicsReport',
            'trInstructionLetter',
            'baseTrInstructionLetter',
        ],
    },
    {
        title: 'Checks & reports',
        keys: ['aml', 'baseAml', 'amlIdentityReport', 'baseAmlIdentityReport', 'creditReport', 'baseCreditReport'],
    },
    {
        title: 'Insurance',
        keys: ['ateCertificate', 'baseAteCertificate'],
    },
]

/**
 * Bucket documents into DOCUMENT_GROUPS, ordered within each group by the key
 * order above. Anything unrecognised falls into a trailing "Other documents"
 * group so a newly added document type can never be hidden.
 */
export const groupDocuments = <T extends { doc_key: string }>(documents: T[]): { title: string; docs: T[] }[] => {
    const remaining = new Map(documents.map((doc) => [doc, true]))
    const groups: { title: string; docs: T[] }[] = []

    for (const group of DOCUMENT_GROUPS) {
        const docs: T[] = []
        for (const key of group.keys) {
            for (const doc of documents) {
                if (remaining.get(doc) && doc.doc_key === key) {
                    docs.push(doc)
                    remaining.set(doc, false)
                }
            }
        }
        if (docs.length > 0) groups.push({ title: group.title, docs })
    }

    const other = documents.filter((doc) => remaining.get(doc))
    if (other.length > 0) groups.push({ title: 'Other documents', docs: other })

    return groups
}
